<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { z } from "zod";
import {
    UiButton,
    UiFormBuilder,
    useToaster,
    type BuilderRowConfig,
} from "@shopexperts/shared";

type ChangePasswordHandler = (currentPassword: string, newPassword: string) => Promise<unknown>;

const props = withDefaults(defineProps<{
    changePassword: ChangePasswordHandler;
    submitLabel?: string;
    successTitle?: string;
    errorFallback?: string;
}>(), {
    submitLabel: "Change Password",
    successTitle: "Password changed successfully",
    errorFallback: "Failed to change password",
});

const passwordChangeSchema = z
    .object({
        current_password: z.string().min(1, "Current password is required"),
        new_password: z.string().min(8, "Password must be at least 8 characters"),
        confirm_password: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.new_password === data.confirm_password, {
        message: "Passwords do not match",
        path: ["confirm_password"],
    });

type PasswordChangeFormValues = z.infer<typeof passwordChangeSchema>;

const toaster = useToaster();
const formRef = useTemplateRef("formRef");
const model = ref<PasswordChangeFormValues>();
const isDirty = ref(false);
const isValid = ref(false);
const isSaving = ref(false);

const initialValues: PasswordChangeFormValues = {
    current_password: "",
    new_password: "",
    confirm_password: "",
};

const rows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            {
                name: "current_password",
                label: "Current Password",
                type: "password",
                props: { type: "password", placeholder: "Enter your current password" },
            },
        ],
    },
    {
        columns: [
            {
                name: "new_password",
                label: "New Password",
                type: "password",
                inlineHint: "Must be at least 8 characters",
                props: { type: "password", placeholder: "Enter your new password" },
            },
        ],
    },
    {
        columns: [
            {
                name: "confirm_password",
                label: "Confirm New Password",
                type: "password",
                props: { type: "password", placeholder: "Confirm your new password" },
            },
        ],
    },
]);

const canSubmit = computed(() => isDirty.value && isValid.value);

function getErrorMessage(err: unknown, fallback: string): string {
    const error = err as { response?: { data?: { message?: string } }; message?: string };
    return error?.response?.data?.message || error?.message || fallback;
}

async function handleSubmit() {
    if (!model.value?.current_password || !model.value?.new_password) {
        return;
    }

    isSaving.value = true;
    try {
        await props.changePassword(model.value.current_password, model.value.new_password);
        formRef.value?.resetForm();
        toaster.create({
            title: props.successTitle,
            type: "success",
        });
    } catch (err: unknown) {
        toaster.create({
            title: getErrorMessage(err, props.errorFallback),
            type: "error",
        });
    } finally {
        isSaving.value = false;
    }
}
</script>

<template>
    <div class="bg-white rounded-lg border border-gray-200 p-6 max-w-[720px] w-full">
        <UiFormBuilder
            ref="formRef"
            :schema="passwordChangeSchema"
            :rows="rows"
            :initial-values="initialValues"
            v-model="model"
            v-model:dirty="isDirty"
            v-model:valid="isValid"
        >
            <template #actions>
                <div class="flex justify-end mt-6">
                    <UiButton
                        variant="primary"
                        :label="submitLabel"
                        :loading="isSaving"
                        :disabled="!canSubmit"
                        @click="handleSubmit"
                    />
                </div>
            </template>
        </UiFormBuilder>
    </div>
</template>
